import { FieldArray, FormikErrors } from 'formik';
import { Plus, Trash2, FileCode } from 'lucide-react';
import { useState, useEffect } from 'react';

import { PathSelector } from '@/react/portainer/gitops/ComposePathField/PathSelector';
import {
  GitFormModel,
  RelativePathModel,
} from '@/react/portainer/gitops/types';

import { FormSection } from '@@/form-components/FormSection';
import { FormControl } from '@@/form-components/FormControl';
import { Button } from '@@/buttons';
import { ExternalLink } from '@@/ExternalLink';
import { FormError } from '@@/form-components/FormError';

import {
  PORTAINER_EDGE_GROUP_VARIABLE,
  PORTAINER_EDGE_ID_VARIABLE,
} from '../../types';
import { FormValues } from '../types';

import { hasDynamicVariable, renderStyledPath } from './helmDynamicVariables';

interface Props {
  valuesFiles: Array<string>;
  gitModel: GitFormModel;
  errors: FormikErrors<FormValues> | Record<string, unknown>;
  onValuesFileChange: (index: number, value: string) => void;
  variant?: 'create' | 'edit';
  relativePath?: RelativePathModel;
}

export function HelmValuesFilesSection({
  valuesFiles,
  gitModel,
  errors,
  onValuesFileChange,
  variant = 'create',
  relativePath,
}: Props) {
  // Local state for instant preview updates (syncs with Formik state)
  const [localValues, setLocalValues] = useState<string[]>(valuesFiles);

  // Sync local state when props change externally
  useEffect(() => {
    setLocalValues(valuesFiles);
  }, [valuesFiles]);

  function handleValueChange(index: number, value: string) {
    // Update local state immediately for instant preview
    setLocalValues((prev) => {
      const newValues = [...prev];
      newValues[index] = value;
      return newValues;
    });
    // Also update Formik state
    onValuesFileChange(index, value);
  }

  function insertDynamicVariable(
    index: number,
    variableType: 'group' | 'device'
  ) {
    if (!relativePath) return;

    const currentValue = localValues[index];
    if (currentValue) {
      return;
    }

    const dynamicVar =
      variableType === 'group'
        ? PORTAINER_EDGE_GROUP_VARIABLE
        : PORTAINER_EDGE_ID_VARIABLE;
    const matchType =
      variableType === 'group'
        ? relativePath.PerDeviceConfigsGroupMatchType
        : relativePath.PerDeviceConfigsMatchType;

    // Only pre-fill up to the dynamic variable, let autocomplete help with the rest
    let suggestedPath = '';
    if (matchType === 'file') {
      // For file matching: path/to/${VAR} (user can add .yaml, .yml, etc.)
      suggestedPath = `${relativePath.PerDeviceConfigsPath}/${dynamicVar}`;
    } else {
      // For directory matching: path/to/${VAR}/ (with trailing slash, user can pick file)
      suggestedPath = `${relativePath.PerDeviceConfigsPath}/${dynamicVar}/`;
    }

    handleValueChange(index, suggestedPath);
  }
  const valuesFilesContent = (
    <FieldArray name="helmValuesFiles">
      {({ push, remove }) => (
        <div>
          {valuesFiles.map((file, index) => {
            // Use local value for instant preview, fall back to prop value
            const displayValue = localValues[index] ?? file;
            return (
              <div
                key={`helm-values-file-${index}`}
                className={variant === 'edit' ? 'mb-2 relative z-[90]' : 'mb-2'}
                style={variant === 'edit' ? { zIndex: 90 - index } : undefined}
              >
                {hasDynamicVariable(displayValue) && (
                  <div className="mb-1">
                    <span className="badge badge-info text-xs">
                      Dynamic:{' '}
                      {displayValue.includes('EDGE_GROUP') ? 'Group' : 'Device'}
                    </span>
                    {renderStyledPath(displayValue)}
                  </div>
                )}
                <div className="flex gap-2 align-top items-start">
                  <div className="flex-1">
                    <PathSelector
                      value={file}
                      onChange={(value) => handleValueChange(index, value)}
                      placeholder="e.g. values/production.yaml"
                      model={gitModel}
                      dirOnly={false}
                      inputId={`helm-values-file-${index}`}
                      hasError={
                        !!(
                          errors.helmValuesFiles &&
                          Array.isArray(errors.helmValuesFiles) &&
                          errors.helmValuesFiles[index]
                        )
                      }
                    />
                  </div>
                  {relativePath?.SupportPerDeviceConfigs &&
                    relativePath.PerDeviceConfigsGroupMatchType && (
                      <Button
                        type="button"
                        color="secondary"
                        className="h-[34px]"
                        data-cy={`helm-values-file-insert-group-var-${index}`}
                        onClick={() => insertDynamicVariable(index, 'group')}
                        icon={FileCode}
                        size="medium"
                        title="Insert Group Variable"
                      >
                        Insert Group
                      </Button>
                    )}
                  {relativePath?.SupportPerDeviceConfigs &&
                    relativePath.PerDeviceConfigsMatchType && (
                      <Button
                        type="button"
                        color="secondary"
                        className="h-[34px]"
                        data-cy={`helm-values-file-insert-device-var-${index}`}
                        onClick={() => insertDynamicVariable(index, 'device')}
                        icon={FileCode}
                        size="medium"
                        title="Insert Device Variable"
                      >
                        Insert Device
                      </Button>
                    )}
                  <Button
                    type="button"
                    color="dangerlight"
                    className="h-[34px]"
                    data-cy={`helm-values-file-remove-${index}`}
                    onClick={() => remove(index)}
                    icon={Trash2}
                    size="medium"
                  >
                    <span className="sr-only">Remove</span>
                  </Button>
                </div>
                {errors.helmValuesFiles &&
                  Array.isArray(errors.helmValuesFiles) &&
                  errors.helmValuesFiles[index] && (
                    <FormError>
                      {typeof errors.helmValuesFiles[index] === 'string'
                        ? errors.helmValuesFiles[index]
                        : ''}
                    </FormError>
                  )}
              </div>
            );
          })}
          <Button
            type="button"
            color="secondary"
            size="small"
            onClick={() => push('')}
            data-cy="helm-values-file-add"
            icon={Plus}
            className="!ml-0"
          >
            Add values file
          </Button>
          {variant === 'edit' && (
            <span className="help-block mt-2">
              Specify user-defined values files to override the default chart
              values. Paths are relative to the Helm chart folder in your Git
              repository.
            </span>
          )}
        </div>
      )}
    </FieldArray>
  );

  if (variant === 'edit') {
    return <>{valuesFilesContent}</>;
  }

  return (
    <FormSection title="Install options">
      <FormControl
        label="Values files"
        tooltip={
          <>
            <p>
              Specify user-defined values files to override the default chart
              values.
            </p>
            <p>
              If multiple values files are specified, they will be merged in the
              order they are specified (last values file takes precedence).
            </p>
            <p>
              You can get more information about the Helm values file format in
              the{' '}
              <ExternalLink
                to="https://helm.sh/docs/chart_template_guide/values_files/"
                data-cy="helm-values-file-format-link"
              >
                official documentation
              </ExternalLink>
              .
            </p>
          </>
        }
      >
        {valuesFilesContent}
      </FormControl>
    </FormSection>
  );
}
